import React, { useEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
export type VideoItem = {
  src: string;
  startAt?: number;
  duration?: number;
};
type VideoSequencerProps = {
  videos: (string | VideoItem)[];
  poster: string;
  caption: string;
  className?: string;
  autoPlay?: boolean;
  showControls?: boolean;
};
export function VideoSequencer({
  videos,
  poster,
  caption,
  className = '',
  autoPlay = true,
  showControls = true
}: VideoSequencerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const parsedVideos = videos.map(v => typeof v === 'string' ? {
    src: v,
    startAt: 1.5,
    duration: 5000
  } : {
    src: v.src,
    startAt: v.startAt ?? 1.5,
    duration: v.duration ?? 5000
  });
  const hasVideos = parsedVideos.length > 0;
  useEffect(() => {
    videoRefs.current.forEach(video => {
      if (video) video.muted = muted;
    });
  }, [muted]);
  function handleEnded() {
    setCurrentIndex(prev => (prev + 1) % parsedVideos.length);
  }
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === currentIndex && playing) {
        video.currentTime = parsedVideos[currentIndex].startAt;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Ignore interruption errors if the cut happens too fast
          });
        }
      } else {
        video.pause();
      }
    });
  }, [currentIndex, playing, parsedVideos]);
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (playing && hasVideos) {
      timeout = setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % parsedVideos.length);
      }, parsedVideos[currentIndex].duration);
    }
    return () => clearTimeout(timeout);
  }, [currentIndex, playing, hasVideos, parsedVideos]);
  function togglePlay() {
    const currentVideo = videoRefs.current[currentIndex];
    if (!currentVideo) return;
    if (currentVideo.paused) {
      void currentVideo.play();
      setPlaying(true);
    } else {
      currentVideo.pause();
      setPlaying(false);
    }
  }
  return <div className={`relative overflow-hidden bg-ink ${className}`} data-id="element-48" data-mp="1hu5cn6myfxn7">
      {hasVideos ? parsedVideos.map((vid, index) => <video key={vid.src} ref={el => videoRefs.current[index] = el} src={vid.src} muted={muted} playsInline onEnded={handleEnded} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${index === currentIndex ? 'z-10 opacity-100' : 'z-0 opacity-0'}`} data-id="element-49" data-mp="2ferxllyhfrgh" data-mpi={index} />) : <img src={poster} alt={caption} className="absolute inset-0 h-full w-full object-cover opacity-75" data-id="element-50" data-mp="p3x3236hilk8" />}

      {showControls && <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-5 md:p-7" data-id="element-51" data-mp="35qv3mt0quvky">
          <span className="pointer-events-auto inline-flex w-fit items-center gap-2 rounded-full bg-ink/85 px-3 py-1.5 text-[0.6rem] uppercase tracking-micro text-lilac" data-id="element-52" data-mp="3aq8085hf3h0q">
            <span className="h-1.5 w-1.5 rounded-full bg-lilac" data-id="element-53" data-mp="3firdt2g3uc5j" />
            {hasVideos ? caption : 'Reel dropping soon'}
          </span>

          {hasVideos && <div className="pointer-events-auto flex items-center gap-2 self-end" data-id="element-54" data-mp="3og1n4i9d3r7t">
              <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause reel' : 'Play reel'} className="rounded-full bg-lilac p-3 text-ink transition-colors hover:bg-sage" data-id="element-55" data-mp="3thha4fv3o7su">
                {playing ? <PauseIcon size={16} data-id="element-56" data-mp="2419kkww5gux9" /> : <PlayIcon size={16} data-id="element-57" data-mp="242kiig2wz8gg" />}
              </button>
              <button type="button" onClick={() => setMuted(value => !value)} aria-label={muted ? 'Unmute reel' : 'Mute reel'} className="rounded-full bg-lilac p-3 text-ink transition-colors hover:bg-sage" data-id="element-58" data-mp="3uimse8mb42eu">
                {muted ? <VolumeXIcon size={16} data-id="element-59" data-mp="3cbwss02ftuw" /> : <Volume2Icon size={16} data-id="element-60" data-mp="3c295b2htysd" />}
              </button>
            </div>}
        </div>}
    </div>;
}